<template>
  <div class="canvas-controls" v-show="ready">
    <div class="buttons">
      <button
        class="btn btn-secondary dropdown-toggle"
        v-if="hasRevisions"
        type="button"
        data-bs-toggle="dropdown"
      >
        {{ $t('RevisionButton.revision', { number: selectedRevision.revision_no }) }}
      </button>
      <div class="dropdown-menu">
        <div
          v-for="rev in revisionsSorted"
          :key="rev.ix"
        >
          <a :href="revisionUrl(rev)" class="dropdown-item">
            {{ $t('RevisionButton.revision', { number: rev.revision_no }) }}:
            <span v-if="rev.uploaded_by" class="text-secondary">
              {{
                $t('RevisionButton.createdAtBy', {
                  date: rev.created_at,
                  person: rev.uploaded_by,
                })
              }}
            </span>
            <span v-else class="text-secondary">
              {{ $t('RevisionButton.createdAt', { date: rev.created_at }) }}
            </span>
            <span v-if="rev.order" class="text-secondary">
              {{ $t('RevisionButton.order', { number: rev.order }) }}
            </span>
          </a>
        </div>
      </div>
      <button
        class="btn btn-secondary"
        :class="modeActiveClass('layers')"
        @click="modeToggle('layers')"
      >
        <i class="fa fa-layer-group" />
      </button>
      <button
        class="btn btn-secondary"
        :class="modeActiveClass('stencil')"
        v-if="smtPadCount > 0"
        @click="modeToggle('stencil')"
      >
        Stencil
      </button>
      <button
        class="btn btn-secondary"
        @click="changeSide"
      >
        <i class="fa fa-sync" />
      </button>
      <button
        class="btn btn-secondary"
        @click="measureToolHint"
      >
        <i class="fa fa-ruler" />
      </button>
    </div>
    <div class="layers bg-secondary rounded p-3 mt-2" v-if="mode === 'layers'">
      <h4>{{ sideActive }}</h4>
      <span
        v-for="layer in this.layers"
        :key="layer.name"
        class="toggler align"
        :class="{ active: layer.selected }"
        @click="toggleLayer(layer)"
      >
        <input type="checkbox" v-model="layer.selected">
        <span class="indicator" :style="layer.indicator" />
        <span>{{ layer.title }}</span>
        <br>
      </span>
    </div>
  </div>
</template>

<script>
import messages from 'locales/BoardInspectorControls';
import _ from 'lodash';

export default {
  name: 'BoardInspectorControls',
  props: {
    stackupLayerCount: Number,
    smtPadCount: String,
    revisions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      bus: this.$root.bus,
      mode: 'preview',
      spin: false,
      layers: [],
      materials: {},
      ready: false,
      revisionMenuVisible: false,
    };
  },
  computed: {
    sideActive() {
      return this.spin ? this.$t('BoardInspectorControls.back') : this.$t('BoardInspectorControls.front');
    },
    selectedRevision() {
      let selected = {};
      this.revisions.forEach((rev) => {
        if (rev.active) {
          selected = rev;
        }
      });
      return selected;
    },
    hasRevisions() {
      return this.revisions.length >= 1;
    },
    revisionsSorted() {
      return _.sortBy(this.revisions, ['ix']).reverse();
    },
  },
  mounted() {
    this.initLayerSelector();
  },
  methods: {
    modeActiveClass(mode) {
      return this.mode === mode ? 'active' : '';
    },
    modeToggle(mode) {
      this.mode = this.mode === mode ? 'preview' : mode;
      this.bus.emit('modeToggle', this.mode);
      // Reset layer selection to stay in sync with viewer
      this.layers.forEach((layer) => {
        layer.selected = true;
      });
    },
    changeSide() {
      this.spin = !this.spin;
      if (this.spin) {
        this.bus.emit('changePerspective', 'back');
      } else {
        this.bus.emit('changePerspective', 'front');
      }
    },
    toggleLayer(layer) {
      layer.selected = !layer.selected;
      this.bus.emit('updateShape', { shape: layer.name, value: layer.selected });
    },
    measureToolHint() {
      this.$root.message = this.$t('BoardInspectorControls.measureHint');
    },
    initLayerSelector() {
      this.bus.on('viewerReady', ({ stackup, materials }) => {
        this.ready = true;
        this.stackup = stackup;
        this.materials = materials;

        this.layers = stackup.map((layer) => {
          if (layer.name.includes('toplayer') || layer.name.includes('bottomlayer') || layer.name.includes('internalplane')) {
            let layerKey = layer.name;
            let layerNo = '';

            if (layerKey.startsWith('internalplane')) {
              layerNo = layerKey.replace('internalplane', '');
              layerKey = 'internalplane';
            }

            return {
              name: layer.name,
              title: this.$t(`BoardInspectorControls.${layerKey}`, { count: layerNo }),
              selected: true,
              indicator: `background-color: #${this.materials[layer.name]?.color?.getHexString()}`,
            };
          }
          return false;
        }).filter((layer) => layer);
      });
    },
    toggleMenu() {
      this.revisionMenuVisible = !this.revisionMenuVisible;
    },
    revisionUrl(rev) {
      return `?revision_no=${rev.revision_no}`
    },
  },
  i18n: { messages },
};
</script>

<style scoped lang="scss">
.canvas-controls {
  position: absolute;
  top: 0px;
  margin-top: 1.5rem;
  margin-left: 1.5rem;
}

.buttons {
  button {
    margin: 0 2px;
  }
}

.layers {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  float: left;
}

.toggler {
  cursor: pointer;
  line-height: 1;
  -webkit-appearance: none;
  font-weight: 400;

  &.active {
    font-weight: 600;
  }

  input {
    margin: 0 0 0.5rem;
  }

  span.indicator {
    height: 1rem;
    width: 1rem;
    display: inline-block;
    margin: 0 0.5rem;
    transform: translateY(3px);
  }
}
</style>
